import { Button, Card, CardContent, TextField } from '@flexui-io/flexui-components';
import { SliderAsyncButton } from '@slider/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Fingerprint, KeyRound, Mail, Smartphone } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { ApiProblemError } from '../app/api/slider-fetch';
import { AuthPage } from '../features/auth/auth-appearance';
import { PasswordField } from '../features/auth/password-field';
import { OneTimeCodeField } from '../features/auth/one-time-code-field';
import { redirectAuthenticatedVisitor, resolveReturnPath } from '../features/auth/session-guard';
import {
  serializeAuthenticationCredential,
  toCredentialRequestOptions,
} from '../features/auth/webauthn';
import {
  createPasskeyAuthenticationOptions,
  getGetCurrentAccountQueryKey,
  useLogin,
  useSendEmailOtp,
  useVerifyEmailOtp,
  useVerifyRecoveryCodeLogin,
  useVerifyTotpLogin,
  verifyPasskeyAuthentication,
} from '../generated/api/auth/auth';
import type { Login200 } from '../generated/api/models';

type LoginFields = { email: string; password: string };
type CodeFields = { code: string };
type MfaChallenge = Extract<Login200, { status: 'MFA_REQUIRED' }>;
type MfaMethod = MfaChallenge['methods'][number];

const LoginSearchSchema = z.object({
  next: z.string().max(2048).optional().catch(undefined),
  // Set by the reset-password page, which replaces itself with the sign-in page
  // so the token-bearing URL is not left behind.
  notice: z.literal('password-reset').optional().catch(undefined),
});

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: zodValidator(LoginSearchSchema),
  beforeLoad: ({ context, search }) =>
    redirectAuthenticatedVisitor({ queryClient: context.queryClient, next: search.next }),
});

function preferredMethod(methods: MfaMethod[]): MfaMethod {
  return (
    (['PASSKEY', 'TOTP', 'EMAIL_OTP', 'RECOVERY_CODE'] as const).find((candidate) =>
      methods.includes(candidate),
    ) ?? methods[0]!
  );
}

function errorKey(error: unknown): string {
  if (!(error instanceof ApiProblemError)) return 'auth.errorGeneric';
  if (error.code === 'AUTH_INVALID_CREDENTIALS') return 'auth.errorInvalidCredentials';
  if (error.code === 'AUTH_MFA_INVALID_CODE') return 'auth.errorInvalidCode';
  if (error.code === 'AUTH_MFA_RATE_LIMITED' || error.code === 'RATE_LIMITED') {
    return 'auth.errorRateLimited';
  }
  if (error.code === 'AUTH_MFA_CHALLENGE_EXPIRED') return 'auth.errorChallengeExpired';
  if (error.code === 'AUTH_PASSKEY_VERIFICATION_FAILED') return 'auth.errorPasskey';
  return 'auth.errorGeneric';
}

function LoginPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { i18n, t } = useTranslation();
  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [codeInputError, setCodeInputError] = useState<'required' | 'incomplete' | null>(null);
  // Lock synchronously: a paste and a manual submit can precede React Query's pending render.
  const verifyInFlight = useRef(false);
  const otpInput = useRef<HTMLInputElement | null>(null);
  const loginForm = useForm<LoginFields>({ defaultValues: { email: '', password: '' } });
  const codeForm = useForm<CodeFields>({ defaultValues: { code: '' } });

  const { next, notice } = Route.useSearch();
  // The signed-in destination is the sanitized relative path the guard captured
  // (or the invitation handoff), so `href` is used instead of a typed `to` route.
  // A handoff return replaces the sign-in entry rather than stacking another
  // token-bearing page onto the history.
  const finish = () => {
    queryClient.removeQueries({ queryKey: getGetCurrentAccountQueryKey() });
    const destination = resolveReturnPath(next);
    void navigate({ href: destination.path, replace: destination.fromHandoff });
  };

  const sendEmail = useSendEmailOtp({
    mutation: {
      onError: setError,
      onSuccess: () => {
        setEmailCodeSent(true);
        setError(null);
      },
    },
  });
  const releaseVerifyLock = () => {
    verifyInFlight.current = false;
  };
  const verifyTotp = useVerifyTotpLogin({
    mutation: { onError: setError, onSuccess: finish, onSettled: releaseVerifyLock },
  });
  const verifyRecovery = useVerifyRecoveryCodeLogin({
    mutation: { onError: setError, onSuccess: finish, onSettled: releaseVerifyLock },
  });
  const verifyEmail = useVerifyEmailOtp({
    mutation: { onError: setError, onSuccess: finish, onSettled: releaseVerifyLock },
  });
  const passkey = useMutation({
    mutationFn: async (activeChallenge: MfaChallenge) => {
      if (!window.PublicKeyCredential) throw new Error('WebAuthn is unavailable');
      const optionsResponse = await createPasskeyAuthenticationOptions({
        challengeToken: activeChallenge.challengeToken,
      });
      const credential = await navigator.credentials.get({
        publicKey: toCredentialRequestOptions(optionsResponse.data),
      });
      if (!(credential instanceof PublicKeyCredential)) throw new Error('No credential returned');
      return verifyPasskeyAuthentication({
        challengeToken: activeChallenge.challengeToken,
        response: serializeAuthenticationCredential(credential),
      });
    },
    onError: setError,
    onSuccess: finish,
  });

  const verificationPending =
    passkey.isPending ||
    sendEmail.isPending ||
    verifyTotp.isPending ||
    verifyRecovery.isPending ||
    verifyEmail.isPending;
  const codeFieldInvalid = Boolean(
    codeInputError || (error && errorKey(error) === 'auth.errorInvalidCode'),
  );
  const codeDigits = new Intl.NumberFormat(i18n.resolvedLanguage ?? i18n.language)
    .format(method === 'EMAIL_OTP' ? 8 : 6);

  useEffect(() => {
    // Email OTP may be briefly disabled while the code is being sent. Focus once it is ready,
    // on first challenge entry, after a method switch and after a failed verification.
    if (challenge && !verificationPending && (method === 'TOTP' || method === 'EMAIL_OTP')) {
      otpInput.current?.focus();
    }
  }, [challenge, method, verificationPending]);

  const selectMethod = (nextMethod: MfaMethod, activeChallenge = challenge) => {
    if (!activeChallenge || verificationPending || verifyInFlight.current) return;
    codeForm.reset();
    setCodeInputError(null);
    setMethod(nextMethod);
    setError(null);
    if (nextMethod === 'PASSKEY') passkey.mutate(activeChallenge);
    if (nextMethod === 'EMAIL_OTP' && !emailCodeSent) {
      sendEmail.mutate({ data: { challengeToken: activeChallenge.challengeToken } });
    }
  };

  const login = useLogin({
    mutation: {
      onError: setError,
      onSuccess: (response) => {
        setError(null);
        if (response.status !== 200) return;
        if (response.data.status === 'AUTHENTICATED') {
          finish();
          return;
        }
        if (response.data.status === 'MFA_ENROLLMENT_REQUIRED') {
          void navigate({ search: { next }, to: '/mfa-enrollment' });
          return;
        }
        const nextMethod = preferredMethod(response.data.methods);
        setChallenge(response.data);
        selectMethod(nextMethod, response.data);
      },
    },
  });

  const submitVerification = (rawCode: string) => {
    if (!challenge || !method || verificationPending || verifyInFlight.current) return;
    const code = rawCode.trim();
    if (method === 'PASSKEY') return;
    if (!code) {
      setCodeInputError('required');
      return;
    }
    if (method !== 'RECOVERY_CODE' && code.length !== (method === 'EMAIL_OTP' ? 8 : 6)) {
      setCodeInputError('incomplete');
      return;
    }
    setCodeInputError(null);
    verifyInFlight.current = true;
    setError(null);
    const data = { challengeToken: challenge.challengeToken, code };
    if (method === 'TOTP') verifyTotp.mutate({ data });
    if (method === 'RECOVERY_CODE') verifyRecovery.mutate({ data });
    if (method === 'EMAIL_OTP') verifyEmail.mutate({ data });
  };
  const submitCode = codeForm.handleSubmit(({ code }) => submitVerification(code));

  return (
    <AuthPage>
      <Link aria-label={t('brand.name')} className="brand-mark auth-brand" to="/">
        <img src="/brand/slider-wordmark.svg" alt="" width="148" height="37" />
      </Link>
      <Card className={`auth-card${challenge ? ' auth-card-verify' : ''}`}>
        <CardContent>
          <p className="eyebrow">{t(challenge ? 'auth.verifyEyebrow' : 'auth.eyebrow')}</p>
          <h1>{t(challenge ? 'auth.verifyTitle' : 'auth.title')}</h1>
          <p className="auth-description">
            {t(challenge ? 'auth.verifyDescription' : 'auth.description')}
          </p>

          {notice === 'password-reset' && !challenge ? (
            <p aria-live="polite" className="auth-description" role="status">
              {t('auth.passwordChangedDescription')}
            </p>
          ) : null}

          {!challenge ? (
            <form
              onSubmit={(event) =>
                void loginForm.handleSubmit((data) => {
                  setError(null);
                  login.mutate({ data });
                })(event)
              }
            >
              <TextField
                autoComplete="email"
                dir="ltr"
                closeIcon={false}
                id="email"
                inputSize="md"
                label={t('auth.email')}
                leftIcon={<Mail aria-hidden="true" size={18} />}
                placeholder={t('auth.emailPlaceholder')}
                required
                type="email"
                {...loginForm.register('email')}
              />
              <PasswordField
                autoComplete="current-password"
                id="password"
                inputSize="md"
                label={t('auth.password')}
                placeholder={t('auth.passwordPlaceholder')}
                required
                {...loginForm.register('password')}
              />
              <Link className="auth-text-link auth-forgot-link" to="/forgot-password">
                {t('auth.forgotPassword')}
              </Link>
              {error ? (
                <p aria-live="polite" className="auth-error" role="alert">
                  {t(errorKey(error))}
                </p>
              ) : null}
              <SliderAsyncButton pending={login.isPending} size="lg" type="submit">
                {t('auth.signIn')}
              </SliderAsyncButton>
            </form>
          ) : (
            <section className="auth-challenge">
              {method === 'PASSKEY' ? (
                <SliderAsyncButton
                  leftIcon={<Fingerprint aria-hidden="true" size={18} />}
                  onClick={() => passkey.mutate(challenge)}
                  pending={passkey.isPending}
                  size="lg"
                  type="button"
                >
                  {t('auth.usePasskey')}
                </SliderAsyncButton>
              ) : (
                <form noValidate onSubmit={(event) => void submitCode(event)}>
                  {method === 'RECOVERY_CODE' ? (
                    <TextField
                      autoComplete="off"
                      dir="ltr"
                      closeIcon={false}
                      disabled={verificationPending}
                      aria-describedby={error || codeInputError ? 'mfa-code-feedback' : undefined}
                      aria-invalid={codeFieldInvalid || undefined}
                      id="verification-code"
                      inputSize="md"
                      label={t('auth.recoveryCode')}
                      placeholder={t('auth.recoveryCodePlaceholder')}
                      leftIcon={<KeyRound aria-hidden="true" size={18} />}
                      required
                      {...codeForm.register('code', {
                        onChange: () => {
                          setCodeInputError(null);
                          setError(null);
                        },
                      })}
                    />
                  ) : (
                    <Controller
                      control={codeForm.control}
                      name="code"
                      render={({ field }) => (
                        <OneTimeCodeField
                          id="verification-code"
                          label={t('auth.verificationCode')}
                          describedBy={error || codeInputError ? 'mfa-code-feedback' : undefined}
                          invalid={codeFieldInvalid}
                          length={method === 'EMAIL_OTP' ? 8 : 6}
                          name={field.name}
                          onBlur={field.onBlur}
                          onChange={(value) => {
                            setCodeInputError(null);
                            setError(null);
                            field.onChange(value);
                          }}
                          onComplete={submitVerification}
                          inputRef={(element) => {
                            field.ref(element);
                            otpInput.current = element;
                          }}
                          disabled={verificationPending}
                          value={field.value}
                        />
                      )}
                    />
                  )}
                  <SliderAsyncButton pending={verificationPending} size="lg" type="submit">
                    {t('auth.verify')}
                  </SliderAsyncButton>
                </form>
              )}

              {error || codeInputError ? (
                <p
                  aria-live="polite"
                  className="auth-error"
                  id="mfa-code-feedback"
                  role="alert"
                >
                  {codeInputError
                    ? t(
                        codeInputError === 'required'
                          ? 'auth.errorCodeRequired'
                          : 'auth.errorCodeIncomplete',
                        { digits: codeDigits },
                      )
                    : t(errorKey(error))}
                </p>
              ) : null}

              <div aria-label={t('auth.otherMethods')} className="auth-methods">
                <p className="auth-methods-label">{t('auth.otherMethods')}</p>
                {challenge.methods
                  .filter((availableMethod) => availableMethod !== method && availableMethod !== 'RECOVERY_CODE')
                  .map((availableMethod) => (
                    <Button
                      key={availableMethod}
                      leftIcon={
                        availableMethod === 'PASSKEY' ? (
                          <Fingerprint aria-hidden="true" size={16} />
                        ) : availableMethod === 'TOTP' ? (
                          <Smartphone aria-hidden="true" size={16} />
                        ) : availableMethod === 'EMAIL_OTP' ? (
                          <Mail aria-hidden="true" size={16} />
                        ) : (
                          <KeyRound aria-hidden="true" size={16} />
                        )
                      }
                      disabled={verificationPending}
                      onClick={() => selectMethod(availableMethod)}
                      size="sm"
                      type="button"
                      variant="outline"
                    >
                      {t(`auth.method.${availableMethod}`)}
                    </Button>
                  ))}
                {challenge.methods.includes('RECOVERY_CODE') && method !== 'RECOVERY_CODE' ? (
                  <Button
                    className="auth-recovery-action"
                    disabled={verificationPending}
                    leftIcon={<KeyRound aria-hidden="true" size={16} />}
                    onClick={() => selectMethod('RECOVERY_CODE')}
                    size="sm"
                    type="button"
                    variant="text"
                  >
                    {t('auth.method.RECOVERY_CODE')}
                  </Button>
                ) : null}
              </div>
              <Button
                disabled={verificationPending}
                onClick={() => {
                  setChallenge(null);
                  setMethod(null);
                  setEmailCodeSent(false);
                  setError(null);
                  setCodeInputError(null);
                }}
                size="sm"
                type="button"
                variant="text"
              >
                {t('auth.backToSignIn')}
              </Button>
            </section>
          )}

          {!challenge ? (
            <p className="auth-legal">
              {t('auth.needAccess')}{' '}
              <a className="auth-text-link" href="mailto:sales@slider.example">
                {t('auth.requestEarlyAccess')}
              </a>
            </p>
          ) : null}
        </CardContent>
      </Card>
    </AuthPage>
  );
}
